/**
 * What is wrong with this arrangement, said in physics.
 *
 * A construction bench is mostly used to build things that do not work, and
 * those are the teaching moments — so nothing here is a validation error.
 * "Rope r2 is invalid" tells a student they have annoyed the software;
 * "nothing sets this rope's tension" tells them what they left out.
 *
 * What is NOT here matters as much as what is. The old bench refused a slack
 * rope, a weight resting on the floor, and a sheave that was not above both
 * its neighbours. None of those is a fault: the first two are states this
 * engine solves for, and the third is geometry it now handles. A fault is only
 * ever an arrangement about which there is nothing true to say.
 */
import { FreeBody } from './freeBody';
import { equilibrium } from './equilibrium';
import { openRopeAt } from './join';
import { defOf } from './registry';
import { Solution } from './solve';
import { Part, PortRef, Problem, Vec2, World } from './types';
import { anyPinned, bodiesOf, bondsOf, isFree, partById, portsOf, released, ropesOf, segmentsOf, spansOf, supportOf } from './world';

const nameOf = (w: World, p: Part | undefined): string =>
    (p ? defOf(p).name(p as never, w) : 'a missing part');

/**
 * A fault is a sentence, and a sentence starts with a capital — unless it
 * starts with something's NAME.
 *
 * On a bench that numbers its own parts, `m₁` and `M₁` are two different
 * blocks. Capitalising the first word of "m₁ is being pulled off T₁" does not
 * tidy the sentence up, it renames the block — and renames it to one that may
 * well also be standing there. Only a plain lower-case word is a word.
 */
const sentence = (t: string): string =>
    (/^[a-z]+(\s|$)/.test(t) ? t.charAt(0).toUpperCase() + t.slice(1) : t);

/** Every part a rope touches or a bond names. */
function connections(w: World): Set<string> {
    const out = new Set<string>();
    ropesOf(w).forEach(r => r.path.forEach(s => out.add(s.partId)));
    bondsOf(w).forEach(b => { out.add(b.hangs.partId); out.add(b.holds.partId); });
    /* STANDING on something counts. It is not a link — it is an edit, and the
     * part carries it rather than a line in `links` — which is why it was
     * missed here, and a block correctly seated on a ramp was told it was
     * "only being held by its pin" and would fall when play was pressed. Both
     * halves were untrue, and together they read as though standing it on the
     * ramp had not worked at all. */
    w.parts.forEach(p => { if (p.support) out.add(p.id); });
    return out;
}

/**
 * How much of the straight run from `a` to `b` lies inside a CONVEX outline.
 *
 * A table or a wedge is a trapezium or a triangle, both convex, so clipping the
 * run against each edge in turn (Cyrus–Beck) gives it exactly. The outline's
 * winding is not assumed: each edge's outward side is the one away from the
 * middle.
 */
function lengthInside(poly: Vec2[], a: Vec2, b: Vec2): number {
    if (poly.length < 3) return 0;
    const mid = poly.reduce((m, v) => ({ x: m.x + v.x / poly.length, y: m.y + v.y / poly.length }), { x: 0, y: 0 });
    const d = { x: b.x - a.x, y: b.y - a.y };
    let t0 = 0, t1 = 1;
    for (let i = 0; i < poly.length; i++) {
        const e0 = poly[i], e1 = poly[(i + 1) % poly.length];
        let n = { x: e1.y - e0.y, y: -(e1.x - e0.x) };
        if (n.x * (mid.x - e0.x) + n.y * (mid.y - e0.y) > 0) n = { x: -n.x, y: -n.y };
        const num = n.x * (a.x - e0.x) + n.y * (a.y - e0.y);
        const den = n.x * d.x + n.y * d.y;
        if (Math.abs(den) < 1e-12) {
            if (num > 0) return 0;
            continue;
        }
        const t = -num / den;
        if (den < 0) t0 = Math.max(t0, t); else t1 = Math.min(t1, t);
        if (t0 >= t1) return 0;
    }
    return (t1 - t0) * Math.hypot(d.x, d.y);
}

export function diagnoseStructure(w: World): Problem[] {
    const out: Problem[] = [];
    const linked = connections(w);

    w.links.forEach(l => {
        const refs: PortRef[] = l.type === 'rope' ? l.path : [l.hangs, l.holds];
        if (refs.some(s => !partById(w, s.partId))) {
            out.push({ level: 'stops', linkIds: [l.id], text: sentence('This join points at a part that is no longer there.') });
        }
    });

    /* Nothing holding it up.
     *
     * One rule and no list of kinds: a part whose body can MOVE and that
     * nothing is joined to would simply fall. A sheave with a rope over it is
     * held by the rope, which is what a movable pulley is. */
    const bodies = bodiesOf(w);
    w.parts.forEach(p => {
        if (linked.has(p.id)) return;
        /* Pinned and joined to nothing. Not a fault — it is exactly how a part
         * arrives, and holding it is why it is not falling — but it is worth
         * saying, because the pin comes out the moment play is pressed and
         * then it WILL fall. */
        if (p.pinned) {
            out.push({
                level: 'warns',
                partIds: [p.id],
                text: sentence(`${nameOf(w, p)} is only being held by its pin. Join it to something, or it will fall when you press play.`),
            });
            return;
        }
        // Resting on a table is being held up. It is not a join, so nothing
        // above has counted it, but it is the answer to "what stops it falling".
        if (p.support && partById(w, p.support)) return;
        // Standing on the floor, the same: a wedge free to slide is not falling.
        if (defOf(p).onFloor) return;
        const body = bodies.find(b => b.partIds.includes(p.id));
        if (!body || !isFree(body)) return;
        if (!defOf(p).bondable) {
            out.push({ level: 'stops', partIds: [p.id], text: sentence(`${nameOf(w, p)} is not on a rope — it is an end with nothing to end.`) });
            return;
        }
        /* Joined to nothing — which is a WARNING and not a fault.
         *
         * A fault is an arrangement about which there is nothing true to say,
         * and there is plenty to say about a weight with nothing holding it:
         * it falls, and lands, and the engine works that out exactly. Calling
         * it a fault also blocked the release that makes it fall, because
         * nothing is computed for an unbuildable bench. */
        out.push({
            level: 'warns',
            partIds: [p.id],
            text: sentence(defOf(p).mass(p as never) > 0
                ? `${nameOf(w, p)} is joined to nothing — there is nothing holding it up.`
                : `${nameOf(w, p)} has nothing bolted to it and no rope on it.`),
        });
    });

    /* Both ends held.
     *
     * A rope every one of whose stops is being held has no tension anybody can
     * work out: two hands on one rope, and how hard each pulls is not in the
     * arrangement — every split satisfies equilibrium equally. Not a fault,
     * and not something to guess at either. It is the prompt for the next
     * step, which is to let go of one of them. */
    const heldBody = new Map(bodies.flatMap(b => b.partIds.map(id => [id, !isFree(b)] as [string, boolean])));
    ropesOf(w).forEach(rope => {
        if (rope.path.length < 2) return;
        if (!rope.path.every(s2 => heldBody.get(s2.partId))) return;
        if (!rope.path.some(s2 => partById(w, s2.partId)?.pinned)) return;
        out.push({
            level: 'warns',
            linkIds: [rope.id],
            text: sentence(`Every part ${rope.label ?? 'this rope'} touches is being held, so nothing decides what it is carrying. Unpin one of them.`),
        });
    });

    ropesOf(w).forEach(rope => {
        if (rope.path.length < 2) {
            out.push({ level: 'stops', linkIds: [rope.id], text: sentence('This rope is not attached to anything yet.') });
            return;
        }
        // A rope that stops on a rim has not ended — it runs over the sheave
        // and goes on to somewhere the student has not said yet.
        const open = openRopeAt(w, rope.path[rope.path.length - 1].partId) ?? openRopeAt(w, rope.path[0].partId);
        if (open && open.rope.id === rope.id) {
            const at = partById(w, (open.atEnd ? rope.path[rope.path.length - 1] : rope.path[0]).partId);
            out.push({
                level: 'stops',
                linkIds: [rope.id],
                partIds: at ? [at.id] : undefined,
                text: sentence(`This rope runs over ${nameOf(w, at)} and stops there. Join it on to whatever it ends at — a hook, a weight, an axle or a rope end.`),
            });
        }
        // A rope passes OVER a guide; it cannot pass through a hook.
        for (let i = 1; i + 1 < rope.path.length; i++) {
            const mid = partById(w, rope.path[i].partId);
            const port = mid && portsOf(w, mid).find(x => x.id === rope.path[i].portId);
            if (mid && port && port.role !== 'guide') {
                out.push({
                    level: 'stops',
                    linkIds: [rope.id],
                    partIds: [mid.id],
                    text: sentence(`This rope runs through ${nameOf(w, mid)} and out the other side. A rope can only run OVER a pulley's rim.`),
                });
            }
        }
        const loose = rope.path.map(s => partById(w, s.partId)).filter(p => p && !defOf(p).bondable);
        if (loose.length > 1) {
            out.push({ level: 'stops', linkIds: [rope.id], text: sentence('You cannot hold both ends of the same rope and expect it to say anything.') });
        }
    });

    /* A run of rope between two parts that are bolted together.
     *
     * They are ONE body: the run can never change length, so the rope can
     * neither pull nor be pulled, and the arrangement stops having a single
     * answer. It happens the moment a rope is taken from a weight to the very
     * sheave that weight hangs from — an easy thing to build, and without this
     * it surfaces only as the general "does not decide what happens", which
     * says nothing about which rope to move. */
    /* Asked of the WHOLE rope, not of each run. A run between two parts of
     * one body is internal to that body — its two pulls on it cancel — but a
     * rope that goes on past them still carries its tension to whatever is
     * outside: two pulleys bolted to one table, with a block hanging off each
     * end of the rope over both, is the ordinary way to build that machine,
     * and it was being stopped because the run across the top joined two
     * parts of the table. A rope is stuck only when EVERY part it touches is
     * one body, because then nothing its tension acts on can move. */
    const owner = new Map<string, string>();
    bodies.forEach(b => b.partIds.forEach(id => owner.set(id, b.id)));
    ropesOf(w).forEach(rope => {
        const stops = [...new Set(rope.path.map(s => s.partId))];
        const one = owner.get(stops[0]);
        if (stops.length < 2 || !one || !stops.every(id => owner.get(id) === one)) return;
        out.push({
            level: 'stops',
            linkIds: [rope.id],
            partIds: stops,
            text: sentence(`${stops.map(id => nameOf(w, partById(w, id))).join(' and ')} are bolted together, so a rope between them can never pull on anything.`),
        });
    });

    /* A rope with less rope in it than the run it has to span.
     *
     * Not reachable by dragging — `movePart` stops at the last position the
     * ropes allow — but every other edit can still ask for it: widening a
     * wedge, growing a sheave so the wrap eats rope, standing a block further
     * down a face. Nothing on this bench stretches, so this is not a rope with
     * a large negative slack, it is an arrangement that cannot exist, and the
     * reader has to be told which rope and by how much. */
    ropesOf(w).forEach(rope => {
        const over = spansOf(w).filter(s => s.ropeId === rope.id)
            .reduce((a, s) => a + Math.max(0, s.now - s.was), 0);
        if (over <= 1e-6) return;
        out.push({
            level: 'stops',
            linkIds: [rope.id],
            text: sentence(`${rope.label ?? 'this rope'} is ${over.toFixed(2)} m shorter than the parts it has to reach — lengthen it, or bring its ends closer together.`),
        });
    });

    /* A rope does not pass through a solid, and one from a block on a face
     * runs along the face — both asked of each straight RUN, since a run is
     * the only thing a rope is between two stops.
     *
     * Through a solid is impossible, so it stops the bench: reshape a wedge
     * under a pulley, or drag a block past the apex, and a run can end up
     * drawn straight through the wood with the numbers still printed as if it
     * could be. Off parallel is only a warning — a bolt opened above a face
     * tilts the rope on purpose — but it is the textbook arrangement, and a
     * reader who meant it should hear when they do not have it. */
    const through = new Set<string>();
    const slanted = new Set<string>();
    const solids = w.parts.filter(p => !!defOf(p).outline);
    segmentsOf(w).forEach(seg => {
        const rope = ropesOf(w).find(r => r.id === seg.ropeId);
        const called = rope?.label ?? 'this rope';
        solids.forEach(s => {
            const inside = lengthInside(defOf(s).outline?.(s as never, w) ?? [], seg.pa, seg.pb);
            const key = `${seg.ropeId}|${s.id}`;
            if (inside <= 1e-3 || through.has(key)) return;
            through.add(key);
            out.push({
                level: 'stops',
                linkIds: [seg.ropeId],
                partIds: [s.id],
                text: sentence(`${called} runs through ${nameOf(w, s)} — ${inside.toFixed(2)} m of it is inside. A rope cannot pass through a solid: move the parts it joins, or run it over a pulley.`),
            });
        });
        [seg.a, seg.b].forEach(end => {
            const p = partById(w, end.partId);
            const sup = p && supportOf(w, p);
            if (!p || !sup || slanted.has(`${seg.ropeId}|${p.id}`)) return;
            const along = sup.axes[0];
            const sin = Math.abs(seg.dir.x * along.y - seg.dir.y * along.x);
            const deg = (Math.asin(Math.min(1, sin)) * 180) / Math.PI;
            if (deg <= 0.5) return;
            slanted.add(`${seg.ropeId}|${p.id}`);
            out.push({
                level: 'warns',
                linkIds: [seg.ropeId],
                partIds: [p.id],
                text: sentence(`${called} leaves ${nameOf(w, p)} at ${deg.toFixed(1)}° to the face it stands on. For the rope to run parallel, fasten the pulley on the surface with its bolt closed to nothing.`),
            });
        });
    });

    /* Setting up is a STATICS problem: a bench being built is standing still.
     * If no set of forces holds it still, that is the answer and the reader is
     * owed it — naming the part and what it is left holding, because "this
     * arrangement has no answer" is true of a great many arrangements and
     * useful about none of them. See §5.1 of the target doc. */
    /* `equilibrium` and not `statics`: ONE solver. For a while the panes
     * answered from the full solve while these warnings answered from the old
     * sequential one, so the bench printed a caution about a bench it was no
     * longer simulating, beside numbers from a bench it was. */
    const still = equilibrium(w);
    if (still.fault) {
        /* An ERROR, not a caution, and with no figures in it.
         *
         * There is no set of forces that holds this arrangement still. What a
         * failed solve leaves on each part is the least-squares residual — the
         * closest it could come to an answer that does not exist — and that is
         * not a force anything is carrying. Printing it as "m₁ is left holding
         * 9.9 N" states a fact about a bench that has no facts of this kind,
         * and on a massless part it states one that cannot be true of any
         * pulley. So the parts are NAMED, because that is what the reader has
         * to change, and nothing is quoted. */
        const who = still.fault.adrift.map(a => a.name);
        out.push({
            level: 'stops',
            whileStill: true,
            partIds: still.fault.adrift.map(a => a.partId),
            text: sentence(still.fault.kind === 'indeterminate'
                ? 'This arrangement does not decide where its parts stand — something here can move with nothing resisting it, or two lines share one load and the ideal model cannot say how they split it. Hold a part, or join it to something that can take the load.'
                : who.length
                    ? `There is no arrangement of forces that holds this still: ${who.join(' and ')} ${who.length === 1 ? 'cannot' : 'cannot'} be balanced where ${who.length === 1 ? 'it stands' : 'they stand'}. Press play to watch what it does instead, or hold a part, or change what is joined to what.`
                    : 'There is no arrangement of forces that holds this still. Press play to watch what it does instead, or hold a part, or change what is joined to what.'),
        });
    }

    if (w.parts.length && bodies.every(b => !isFree(b))) {
        out.push({
            level: 'warns',
            text: sentence(anyPinned(w)
                // Being held is the point of being held; it is not a fault.
                ? 'Everything here is pinned. Press play to pull the pins out.'
                : 'Everything here is bolted down — nothing can move.'),
        });
    }
    return out;
}

export function diagnoseSolution(w: World, sol: Solution, fbs?: FreeBody[]): Problem[] {
    const out: Problem[] = [];
    if (sol.singular) {
        out.push({
            level: 'stops',
            text: sentence('This arrangement does not decide what happens — either something can move with nothing resisting it, or two ropes are sharing one load and the ideal model cannot say how they split it.'),
        });
        return out;
    }
    /* A slack rope is a STATE, not a fault — that is the whole point of the
     * engine. It is still worth saying out loud, because a reader looking for
     * a tension and finding none should be told why there is none. */
    const spans = spansOf(w);
    ropesOf(w).forEach(rope => {
        const mine = spans.filter(sp => sp.ropeId === rope.id);
        // Slack when NO span of it is carrying anything: a rope divided by
        // something that grips can be taut on one side and loose on the other.
        if (mine.length && mine.every(sp => sol.force[sp.id] === undefined)) {
            out.push({ level: 'warns', linkIds: [rope.id], text: sentence(`${rope.label ?? 'this rope'} is slack — it is carrying nothing.`) });
        }
    });

    /* A surface that is PULLING.
     *
     * A block only rests on a table until something lifts it off, and a
     * negative normal force is the arithmetic saying that moment has come. The
     * bench cannot follow it there — what a part rests on is a stored
     * relationship and taking it off is an edit — so the honest thing is to
     * say which number stopped meaning what it says, rather than print it. */
    w.parts.filter(p => p.support).forEach(p => {
        const N = sol.force[`rest:${p.id}`];
        if (N === undefined || N >= -1e-9) return;
        const on = partById(w, p.support as string);
        out.push({
            level: 'warns',
            partIds: [p.id],
            text: sentence(`${nameOf(w, partById(w, p.id))} is being pulled off ${nameOf(w, on)} — it would lift, and this bench keeps it down. The ${Math.abs(N).toFixed(1)} N is what would have to hold it there.`),
        });
    });
    /* The warning that used to stand here said "nothing here hangs at an angle
     * and stays there — this bench holds it on one line and answers the
     * up-and-down question only". That was true of an engine whose hanging
     * bodies had a single vertical coordinate, so a sideways pull had nowhere
     * to act and was dropped. It is not true now: a part's POSITION is an
     * unknown of the solve, so things hang at an angle and stay there and the
     * angle is an answer rather than an apology. Removed rather than reworded,
     * because there is nothing left to caution about. */
    return out;
}

export function diagnose(w: World, sol?: Solution, fbs?: FreeBody[]): Problem[] {
    const structural = diagnoseStructure(w);
    if (structural.some(p => p.level === 'stops') || !sol) return structural;
    return [...structural, ...diagnoseSolution(w, sol, fbs)];
}

/**
 * What a joining point still needs — the same rules as the checks above, asked
 * one point at a time.
 *
 * It lives beside `diagnoseStructure` because it IS those rules: a point
 * marked `needed` is one whose absence that function turns into a fault, and a
 * bench that coloured a dot green while the alert beneath it called the part
 * broken would be arguing with itself. Changing one means changing the other.
 */
export type JoinNeed = 'joined' | 'optional' | 'needed';

export function joinNeed(w: World, ref: PortRef): JoinNeed {
    const part = partById(w, ref.partId);
    if (!part) return 'optional';
    const port = portsOf(w, part).find(p => p.id === ref.portId);
    if (!port) return 'optional';

    if (port.role === 'guide') {
        // A rope that stops here is the one case where a rim MUST be joined on.
        const open = openRopeAt(w, part.id);
        if (open) return 'needed';
        const over = ropesOf(w).some(r => r.path.slice(1, -1).some(s => s.partId === part.id));
        return over ? 'joined' : 'optional';
    }

    const named = w.links.some(l => (l.type === 'rope' ? l.path : [l.hangs, l.holds])
        .some(s => s.partId === ref.partId && s.portId === ref.portId));
    if (named) return 'joined';
    // A rope end exists only to end a rope.
    if (!defOf(part).bondable) return 'needed';
    if (connections(w).has(part.id)) return 'optional';
    /* Asked of the bench with the pins OUT. A pin is a hand, not a machine:
     * something held by one still needs joining to something, because the hand
     * comes off the moment play is pressed. */
    const loose = released(w);
    const body = bodiesOf(loose).find(b => b.partIds.includes(part.id));
    return body && isFree(body) ? 'needed' : 'optional';
}

/** Can the numbers be trusted at all? */
/**
 * Can this bench be simulated at all?
 *
 * Not "does it stand still" — a bench that cannot stand still is exactly the
 * one worth running, and `whileStill` marks the reports that say so. What
 * makes a bench unbuildable is an arrangement the engine cannot be asked
 * about: a rope threaded halfway, a line between two parts that are already
 * one body.
 */
export const isBuildable = (problems: Problem[]): boolean =>
    !problems.some(p => p.level === 'stops' && !p.whileStill);
